import React, { useEffect, useState, useRef } from 'react';
import { useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Landing route for the Facebook OAuth redirect
 * (https://ol.semprion.com/facebook-oauth-callback?code=...&state=...).
 * Reads code + state, derives the org from the state prefix, and POSTs to the
 * backend to complete the token exchange. Without this route the SPA falls
 * through to /dashboard and the code is silently discarded.
 */
export default function FacebookOAuthCallback() {
  const { search } = useLocation();
  const [phase, setPhase] = useState('working');
  const [text, setText] = useState('Completing Facebook connection…');
  const [pages, setPages] = useState([]);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const params = new URLSearchParams(search);
    const code = params.get('code');
    const oauthState = params.get('state') || '';
    const fbErr = params.get('error_description') || params.get('error');
    if (fbErr) { setPhase('error'); setText(`Facebook returned: ${fbErr}`); return; }
    if (!code) { setPhase('error'); setText('No authorization code in the callback URL.'); return; }
    const orgId = oauthState.split('::')[0];
    if (!orgId) { setPhase('error'); setText('Missing organization in the OAuth state parameter.'); return; }
    (async () => {
      try {
        const { data } = await axios.post(
          `${API}/api/facebook/${orgId}/oauth/callback`,
          { code, state: oauthState },
          { withCredentials: true },
        );
        setPhase('ok');
        setText(`Connected Facebook Page: ${data.page_name || data.page_id}`);
        // Surfacing every Page the grant covered lets the operator set the
        // "Expected Page ID" guard without hunting for the number.
        setPages(data.pages_available || []);
      } catch (e) {
        setPhase('error');
        setText(e.response?.data?.detail || e.message || 'Connection failed.');
      }
    })();
  }, [search]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 p-6" data-testid="facebook-oauth-callback-page">
      <div className="w-full max-w-md rounded-xl border border-slate-800 bg-slate-900 p-6 text-center">
        <h1 className="text-lg font-semibold text-slate-100 mb-3">Facebook connection</h1>
        <div className="flex justify-center mb-3">
          {phase === 'working' && <Loader2 className="h-6 w-6 animate-spin text-slate-400" />}
          {phase === 'ok' && <CheckCircle2 className="h-6 w-6 text-emerald-400" />}
          {phase === 'error' && <AlertTriangle className="h-6 w-6 text-amber-400" />}
        </div>
        <p className={`text-sm ${phase === 'error' ? 'text-amber-300' : 'text-slate-300'}`}>{text}</p>

        {pages.length > 0 && (
          <div className="mt-4 text-left">
            <p className="text-[11px] font-bold text-slate-300 mb-1">Pages this grant covered</p>
            <ul className="space-y-1">
              {pages.map((p) => (
                <li key={p.id} className="text-[11px] text-slate-400">
                  {p.name} — <code className="text-slate-300">{p.id}</code>
                </li>
              ))}
            </ul>
            <p className="text-[10px] text-slate-500 mt-2">
              Paste the right ID into “Expected Page ID” in Tools → Facebook to lock future
              connects to that Page.
            </p>
          </div>
        )}

        <Link to="/dashboard" className="mt-5 inline-block text-xs text-blue-400 hover:underline">
          Return to dashboard
        </Link>
      </div>
    </div>
  );
}
